import { normalizeRoleValue } from "./roles";

/**
 * Configuration mapping page paths to the list of roles authorized to view them.
 * If a path is not in this object, it is considered public by default.
 */
export const AUTHORIZED_PAGES: Record<string, string[]> = {
  "/mail": ["email_account"],
  "/admin/campaigns": ["developer", "administrator", "tenant_admin", "super_admin"],
  "/admin/coupons": ["developer", "administrator", "tenant_admin", "super_admin"],
  "/admin/site-settings": ["developer", "administrator", "tenant_admin", "super_admin"],
  "/admin": ["administrator", "tenant_admin", "super_admin"],
};

function matchingRule(path: string) {
  const cleanPath = "/" + path.replace(/^\/|\/$/g, "");
  return Object.keys(AUTHORIZED_PAGES)
    .sort((a, b) => b.length - a.length)
    .find((rule) => {
      const cleanRule = "/" + rule.replace(/^\/|\/$/g, "");
      return cleanPath === cleanRule || cleanPath.startsWith(cleanRule + "/");
    });
}

export function isProtectedPath(path: string): boolean {
  return Boolean(matchingRule(path));
}

/**
 * Helper to check if a role is authorized to access a specific path.
 * Supports exact path matches and prefix subpath matches (e.g. /admin/edit-catalog under /admin).
 */
export function isAuthorized(path: string, userRoleOrRoles: string | string[] | undefined): boolean {
  const ruleKey = matchingRule(path);

  if (!ruleKey) {
    return true; // Page is public by default
  }

  if (!userRoleOrRoles) {
    return false; // User not logged in, but page requires a role
  }

  const allowedRoles = AUTHORIZED_PAGES[ruleKey].map(normalizeRoleValue);
  const userRoles = (Array.isArray(userRoleOrRoles) ? userRoleOrRoles : [userRoleOrRoles])
    .map((role) => normalizeRoleValue(String(role)));

  // Check if any of user's roles match allowed roles (supporting super_admin as admin)
  return allowedRoles.some(
    (role) =>
      userRoles.includes(role) ||
      (role === "administrator" && userRoles.includes("super_admin"))
  );
}
